// src/components/AuthProvider.tsx
// Simplified auth provider — uses unified /auth endpoint, no Supabase Auth sessions
// ~250 lines (was ~1155 lines)

import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTelegram } from '@/hooks/useTelegram'
import { useAuthStore, useStudioStore } from '@/store'
import { supabase } from '@/lib/supabase'
import { callAuth } from '@/lib/authClient'
import { withAuthRecovery } from '@/lib/authRecovery'
import { LoadingScreen } from './LoadingScreen'
import { EmailPromptModal } from './EmailPromptModal'
import { mapRawStudiosToStore } from '@/lib/studioMapper'
import { formatAuthErrorMessage } from '@/lib/authErrorMessages'
import { isMockInitData, resolveSubmittableInitData } from '@/lib/telegramInitData'
import { ensureDevActorResolved } from '@/lib/devActor'
import {
  clearPendingInvite,
  selectInviteTokenForAuth,
  selectPendingInvite,
  snapshotPendingInvite
} from '@/lib/invitePersistence'

const ACCESS_REFRESH_INTERVAL_MS = 2 * 60 * 1000

function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4 || 4)) % 4)
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

function isTokenExpired(token: string): boolean {
  const exp = decodeTokenPayload(token)?.exp
  return typeof exp !== 'number' || exp * 1000 <= Date.now()
}

function getTokenSubject(token: string): string | null {
  const sub = decodeTokenPayload(token)?.sub
  return typeof sub === 'string' && sub.length > 0 ? sub : null
}

/**
 * Мок-вход для локальной разработки: telegram_id из `VITE_MOCK_TELEGRAM_ID`
 * либо email из `VITE_MOCK_EMAIL` (каталог входов — в `.env.local`).
 *
 * Значения читаются на верхнем уровне намеренно: Vite подставляет их на этапе
 * сборки, в прод-сборке `import.meta.env.DEV` = false, поэтому константа
 * сворачивается в пустую строку и минификатор выкидывает все ветки ниже.
 *
 * ⚠️ Подписывающая логика живёт в `@/lib/devMockAuth`, и от попадания секретов
 * в бандл её защищает НЕ динамический импорт (Rollup всё равно эмитит чанк —
 * проверено 22.08.2026), а подмена модуля заглушкой в `vite.config.js` для
 * любой сборки. Динамический импорт здесь нужен для другого: чтобы код мока
 * не попадал в основной чанк дев-сборки и не мешался в отладке.
 */
const DEV_MOCK_EMAIL = import.meta.env.DEV
  ? String(import.meta.env.VITE_MOCK_EMAIL ?? '').trim().toLowerCase()
  : ''

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { getInitData, isInTelegram, isReady: isTelegramReady } = useTelegram()
  const { login, logout, setLoading, isLoading, isAuthenticated, user } = useAuthStore()
  const {
    setStudios,
    studios,
    beginStudiosSync,
    failStudiosSync,
  } = useStudioStore()

  const isMountedRef = useRef(true)
  const studiosRef = useRef(studios)
  const accessRefreshInFlightRef = useRef(false)
  const lastAccessRefreshAtRef = useRef(0)
  const hasAttemptedAuth = useRef(false)
  const isAuthenticating = useRef(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const shouldRedirectToSite = authError?.includes('этот аккаунт еще не привязан как способ входа') ?? false

  async function fetchStudiosForUser(userId: string, telegramId?: number | null) {
    console.log('AuthProvider: loading studios for user', userId)
    let canonicalEmptyConfirmed = false
    let managementAccessFound = false
    let managementStudiosConfirmed = false

    // Senior access is a role overlay on the ordinary team studios. It must
    // never be loaded as a second studio source: doing so made invite and
    // routing logic maintain two competing access lists.
    const applyTeacherRoles = async (regularRows: any[]): Promise<any[]> => {
      const senior = await withAuthRecovery(() => (supabase as any).rpc('list_my_approved_teacher_partners'))
      if (senior.error) {
        console.warn('AuthProvider: list_my_approved_teacher_partners failed', senior.error)
        return mapRawStudiosToStore(regularRows)
      }
      const seniorPartners = new Set((senior.data || []).map((row: any) => Number(row.partner_number)))
      return mapRawStudiosToStore(regularRows.map((row: any) => (
        seniorPartners.has(Number(row.partner_number))
          ? { ...row, team_role: 'senior_teacher' }
          : row
      )))
    }

    const primary = await withAuthRecovery(() => (supabase as any).rpc('get_my_studios'))
    if (!primary.error) {
      canonicalEmptyConfirmed = true
      if (primary.data?.length) {
        console.log('AuthProvider: get_my_studios returned', primary.data.length, 'studios')
        return await applyTeacherRoles(primary.data)
      }
      console.warn('AuthProvider: get_my_studios returned no studios, trying UUID fallbacks')
    } else {
      console.warn('AuthProvider: get_my_studios failed', primary.error)
    }

    const uuidFallback = await withAuthRecovery(() => (supabase as any).rpc('get_user_studios_by_uuid', { p_user_id: userId }))
    if (!uuidFallback.error) {
      canonicalEmptyConfirmed = true
      if (uuidFallback.data?.length) {
        console.log('AuthProvider: get_user_studios_by_uuid returned', uuidFallback.data.length, 'studios')
        return await applyTeacherRoles(uuidFallback.data)
      }
    } else {
      console.warn('AuthProvider: get_user_studios_by_uuid failed', uuidFallback.error)
    }

    const mcAccessByUuid = await withAuthRecovery(() => (supabase as any)
      .from('management_company_access')
      .select('access_level')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle())

    if (mcAccessByUuid.error) {
      console.warn('AuthProvider: management_company_access lookup by user_id failed', mcAccessByUuid.error)
    }

    if (mcAccessByUuid.data) {
      managementAccessFound = true
      console.log('AuthProvider: management company access found by user_id, loading studios via v2 fallback')
      const mcStudios = await withAuthRecovery(() => (supabase as any).rpc('get_management_company_studios_v2', { p_user_id: userId }))
      if (!mcStudios.error && mcStudios.data?.length) {
        console.log('AuthProvider: get_management_company_studios_v2 returned', mcStudios.data.length, 'studios')
        return mapRawStudiosToStore(mcStudios.data)
      }
      if (mcStudios.error) {
        console.warn('AuthProvider: get_management_company_studios_v2 failed', mcStudios.error)
      } else {
        managementStudiosConfirmed = true
        canonicalEmptyConfirmed = true
      }
    }

    if (!telegramId) {
      if (managementAccessFound && !managementStudiosConfirmed) throw new Error('Не удалось проверить доступ к студиям')
      if (canonicalEmptyConfirmed) return await applyTeacherRoles([])
      throw new Error('Не удалось проверить доступ к студиям')
    }

    const mcAccessByTelegram = await withAuthRecovery(() => (supabase as any)
      .from('management_company_access')
      .select('access_level')
      .eq('user_telegram_id', telegramId)
      .eq('is_active', true)
      .maybeSingle())

    if (mcAccessByTelegram.error) {
      console.warn('AuthProvider: management_company_access lookup by telegram_id failed', mcAccessByTelegram.error)
    }

    if (!mcAccessByTelegram.data) {
      if (managementAccessFound && !managementStudiosConfirmed) throw new Error('Не удалось проверить доступ к студиям')
      if (canonicalEmptyConfirmed) return await applyTeacherRoles([])
      throw new Error('Не удалось проверить доступ к студиям')
    }

    managementAccessFound = true
    const legacyStudios = await withAuthRecovery(() => (supabase as any).rpc('get_user_studios_with_access_v2', { user_telegram_id: telegramId }))
    if (!legacyStudios.error && legacyStudios.data?.length) {
      return mapRawStudiosToStore(legacyStudios.data)
    }
    if (legacyStudios.error) {
      console.warn('AuthProvider: get_user_studios_with_access_v2 failed', legacyStudios.error)
    } else {
      managementStudiosConfirmed = true
      canonicalEmptyConfirmed = true
    }

    if (managementAccessFound && !managementStudiosConfirmed) throw new Error('Не удалось проверить доступ к студиям')
    if (canonicalEmptyConfirmed) return await applyTeacherRoles([])
    throw new Error('Не удалось проверить доступ к студиям')
  }

  // The first route must use the same canonical access model as subsequent
  // refreshes. In particular, senior-teacher access is an overlay on ordinary
  // team studios and is not guaranteed to be present in the auth response.
  async function resolveInitialStudios(userId: string, telegramId: number | null | undefined, rawRows: any[]) {
    const fallback = mapRawStudiosToStore(rawRows || [])
    try {
      return await fetchStudiosForUser(userId, telegramId)
    } catch (error) {
      console.warn('AuthProvider: failed to resolve canonical access on sign-in', error)
      return fallback
    }
  }

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  useEffect(() => {
    studiosRef.current = studios
  }, [studios])

  // ─── Main auth effect ───────────────────────────────────────────────────────
  useEffect(() => {
    // Wait for Telegram WebApp to finish initializing before attempting auth.
    // Without this, the first render may see isInTelegram=false and call logout(),
    // setting hasAttemptedAuth=true and blocking the retry when Telegram is ready.
    if (!isTelegramReady) return

    // Skip auth on certain routes
    const isInviteRoute = location.pathname.startsWith('/invite/')
    const isWebLoginRoute = location.pathname === '/login'
    const isEmailVerifiedRoute = location.pathname === '/email-verified'
    const hasToken = !!localStorage.getItem('auth_token')
    // На /login пропускаем auth только если нет токена и не в Telegram
    // (если токен есть — пытаемся восстановить сессию даже со страницы логина)
    if (isInviteRoute) {
      setAuthError(null)
      setLoading(false)
      return
    }
    if (isEmailVerifiedRoute || (isWebLoginRoute && !isInTelegram && !hasToken && !DEV_MOCK_EMAIL)) {
      setLoading(false)
      return
    }

    const existingToken = localStorage.getItem('auth_token')
    const rawInitData = (window as any)?.Telegram?.WebApp?.initData ?? null
    const telegramData = getInitData()
    const isMockSession = import.meta.env.DEV && isMockInitData(rawInitData)
    const mockTelegramId = telegramData?.user?.id ?? null
    const shouldForceMockTelegramReauth = Boolean(
      isMockSession &&
      mockTelegramId &&
      user?.telegram_id !== mockTelegramId
    )
    // Тот же случай для email-мока: в .env.local поменяли адрес, а в localStorage
    // лежит токен прежнего аккаунта — без этого переключение молча не срабатывает.
    const shouldForceMockEmailReauth = Boolean(
      DEV_MOCK_EMAIL &&
      (user?.email ?? '').trim().toLowerCase() !== DEV_MOCK_EMAIL
    )
    const shouldForceMockReauth = shouldForceMockTelegramReauth || shouldForceMockEmailReauth

    if (shouldForceMockReauth) {
      console.log(
        'AuthProvider: forcing mock re-auth',
        shouldForceMockEmailReauth ? `for email ${DEV_MOCK_EMAIL}` : `for telegram_id ${mockTelegramId}`,
        'instead of stale user', user?.email ?? user?.telegram_id
      )
      localStorage.removeItem('auth_token')
      sessionStorage.removeItem('auth_token')
    }

    if (isAuthenticated && !shouldForceMockReauth) {
      // Already authenticated with valid token — nothing to do
      const restoredIdentityMatchesToken = Boolean(
        existingToken && user?.id && getTokenSubject(existingToken) === user.id
      )
      if (existingToken && !isTokenExpired(existingToken) && restoredIdentityMatchesToken) {
        setLoading(false)
        return
      }

      // Expired token or stale/incomplete persisted user — restore the canonical
      // identity and studios from /auth before rendering private routes.
      if (existingToken) {
        silentRefresh(existingToken)
        return
      }

      // No token but isAuthenticated (stale Zustand state) — fall through to re-auth
      if (!isInTelegram) {
        logout()
        setLoading(false)
        return
      }
      // In Telegram: remove stale flag, continue to tg-auth below
      localStorage.removeItem('auth_token')
    } else if (existingToken && !shouldForceMockReauth) {
      // Not authenticated (Zustand state reset), but token exists in localStorage.
      // Try to restore session via silent refresh — works for both valid and expired tokens
      // (30-day JWT refresh endpoint accepts expired tokens, checking signature only).
      silentRefresh(existingToken)
      return
    }

    if (hasAttemptedAuth.current || isAuthenticating.current) {
      setLoading(false)
      return
    }

    hasAttemptedAuth.current = true
    isAuthenticating.current = true
    authenticate()
  }, [location.pathname, isInTelegram, isTelegramReady])

  // ─── Silent refresh ─────────────────────────────────────────────────────────
  async function silentRefresh(token: string) {
    try {
      const result = await callAuth('refresh', { currentToken: token })
      if (!isMountedRef.current) return
      localStorage.setItem('auth_token', result.token)
      // Refresh responses contain the ordinary team access only. Re-read the
      // canonical access model so an approved senior-teacher overlay is
      // applied before routing decides which first screen to open.
      let mappedStudios = mapRawStudiosToStore(result.studios || [])
      try {
        mappedStudios = await fetchStudiosForUser(
          result.user.id,
          (result.user as any).telegram_id ?? null
        )
      } catch (accessError) {
        // A successful token refresh must not turn into a logout merely
        // because the optional access overlay could not be re-read.
        console.warn('AuthProvider: failed to refresh access overlay after token refresh', accessError)
      }
      setStudios(mappedStudios)
      login({ ...result.user, studios: mappedStudios } as any)
    } catch {
      if (!isMountedRef.current) return
      // Refresh failed
      if (isInTelegram) {
        // In Telegram — re-auth with initData
        localStorage.removeItem('auth_token')
        hasAttemptedAuth.current = false
        isAuthenticating.current = false
        authenticate()
        return
      }
      // In browser — force re-login
      logout()
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }

  // ─── Main authentication ────────────────────────────────────────────────────
  async function authenticate() {
    try {
      if (!isMountedRef.current) return
      setLoading(true)

      // ─── Dev mode: вход по email ────────────────────────────────────────────
      // Приоритетнее телеграмного мока: у педагогики telegram_id нет вовсе,
      // и подставлять ей чужой мок-id было бы входом не под тем человеком.
      if (import.meta.env.DEV && DEV_MOCK_EMAIL) {
        await authenticateDevEmail(DEV_MOCK_EMAIL)
        return
      }

      const telegramData = getInitData()
      const webAppInitData = (window as any)?.Telegram?.WebApp?.initData ?? null
      const launchFallbackRaw = (telegramData as any)?.raw ?? null

      // ─── Dev mode with mock ─────────────────────────────────────────────────
      const hasMockInitData = isMockInitData(webAppInitData) || isMockInitData(launchFallbackRaw)

      if (import.meta.env.DEV && hasMockInitData && telegramData?.user) {
        await authenticateDevMode(telegramData.user)
        return
      }

      // ─── Production Telegram auth ───────────────────────────────────────────
      // Мок-initData на сервер не уходит: её подпись заведомо не сойдётся, а 401 превращался
      // в экран «Ошибка аутентификации» поверх страницы, на которую человек шёл.
      // Нет пригодной initData — это не ошибка, а обычный браузер: показываем экран входа.
      const rawInitData = resolveSubmittableInitData({
        webAppInitData,
        launchFallbackRaw,
        allowMock: false
      })

      if (!telegramData?.user || !rawInitData) {
        if (isMountedRef.current) logout()
        return
      }

      const explicitInviteToken = selectInviteTokenForAuth(
        snapshotPendingInvite()
      )

      const result = await callAuth('telegram', {
        initData: rawInitData,
        invite_token: explicitInviteToken,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        appVersion: import.meta.env.VITE_APP_VERSION || null,
        telegramWebAppVersion: (window as any)?.Telegram?.WebApp?.version || null,
        telegramPlatform: (window as any)?.Telegram?.WebApp?.platform || null
      })

      if (!isMountedRef.current) return

      localStorage.setItem('auth_token', result.token)
      const mappedStudios = await resolveInitialStudios(
        result.user.id,
        (result.user as any).telegram_id ?? null,
        result.studios || []
      )
      if (!isMountedRef.current) return
      setStudios(mappedStudios)
      login({
        ...result.user,
        created_at: (result.user as any).created_at || new Date().toISOString(),
        updated_at: (result.user as any).updated_at || new Date().toISOString(),
        studios: mappedStudios
      } as any)
    } catch (error) {
      console.error('AuthProvider: Authentication error:', error)
      if (isMountedRef.current) {
        const msg = error instanceof Error ? error.message : 'Unknown error'
        if (msg === 'initData is too old') {
          // Stale initData — just show landing, not an error
          setLoading(false)
          return
        }
        if (location.pathname.startsWith('/invite/') && msg.includes('invite_required')) {
          setAuthError(null)
          logout()
          return
        }
        setAuthError(formatAuthErrorMessage(error, 'telegram'))
        logout()
      }
    } finally {
      isAuthenticating.current = false
      if (isMountedRef.current) setLoading(false)
    }
  }

  // ─── Dev mode helper: вход по email ─────────────────────────────────────────
  // Ровно тот же серверный путь, что и переход по ссылке из письма
  // (`method: 'email-verify'`), только HMAC подписываем сами — письмо не нужно.
  // ⚠️ Неизвестный адрес сервер не отвергнет, а СОЗДАСТ пользователя
  // (`upsertEmailUser` в edge-функции), поэтому в `.env.local` кладём только адрес
  // из каталога входов, а не набираем по памяти.
  async function authenticateDevEmail(email: string) {
    try {
      const { createMockEmailCredentials } = await import('@/lib/devMockAuth')
      const credentials = await createMockEmailCredentials(email)
      const result = await callAuth('email-verify', { ...credentials })

      if (!isMountedRef.current) return

      localStorage.setItem('auth_token', result.token)
      const mappedStudios = await resolveInitialStudios(
        result.user.id,
        (result.user as any).telegram_id ?? null,
        result.studios || []
      )
      if (!isMountedRef.current) return
      setStudios(mappedStudios)
      login({
        ...result.user,
        created_at: (result.user as any).created_at || new Date().toISOString(),
        updated_at: (result.user as any).updated_at || new Date().toISOString(),
        studios: mappedStudios
      } as any)
      console.log('AuthProvider: DEV email mock signed in as', result.user?.email)
    } catch (e) {
      console.error('DEV EMAIL MOCK auth failed:', e)
      if (isMountedRef.current) {
        setAuthError(formatAuthErrorMessage(e, 'email'))
        logout()
      }
    } finally {
      isAuthenticating.current = false
      if (isMountedRef.current) setLoading(false)
    }
  }

  // ─── Dev mode helper ────────────────────────────────────────────────────────
  async function authenticateDevMode(mockUser: any) {
    try {
      const { createSignedMockInitData } = await import('@/lib/devMockAuth')
      const signedInitData = await createSignedMockInitData(mockUser)
      const explicitInviteToken = selectInviteTokenForAuth(
        snapshotPendingInvite()
      )
      const result = await callAuth('telegram', {
        initData: signedInitData,
        invite_token: explicitInviteToken,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        appVersion: import.meta.env.VITE_APP_VERSION || null,
        telegramWebAppVersion: 'dev-mock',
        telegramPlatform: 'web'
      })

      if (isMountedRef.current) {
        localStorage.setItem('auth_token', result.token)
        const mappedStudios = await resolveInitialStudios(
          result.user.id,
          (result.user as any).telegram_id ?? null,
          result.studios || []
        )
        if (!isMountedRef.current) return
        setStudios(mappedStudios)
        login({
          ...result.user,
          created_at: (result.user as any).created_at || new Date().toISOString(),
          updated_at: (result.user as any).updated_at || new Date().toISOString(),
          studios: mappedStudios
        } as any)
      }
    } catch (e) {
      console.error('DEV MODE auth failed:', e)
      if (isMountedRef.current) logout()
    } finally {
      isAuthenticating.current = false
      if (isMountedRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return

    const sameStudioAccess = (left: typeof studios, right: typeof studios) => {
      if (left.length !== right.length) return false
      return left.every((studio, index) => {
        const nextStudio = right[index]
        return (
          nextStudio?.studio_internal_id === studio.studio_internal_id &&
          nextStudio?.status_code === studio.status_code &&
          nextStudio?.access_kind === studio.access_kind &&
          nextStudio?.role === studio.role &&
          nextStudio?.partner_number === studio.partner_number
        )
      })
    }

    const refreshStudiosAccess = async (force = false) => {
      if (accessRefreshInFlightRef.current) return

      const now = Date.now()
      if (!force && now - lastAccessRefreshAtRef.current < ACCESS_REFRESH_INTERVAL_MS) {
        return
      }

      accessRefreshInFlightRef.current = true
      lastAccessRefreshAtRef.current = now

      const accessStateBeforeRefresh = useStudioStore.getState()
      if (accessStateBeforeRefresh.studios.length === 0) {
        beginStudiosSync()
      }

      try {
        const loadedStudios = await fetchStudiosForUser(user.id, user.telegram_id)
        if (!isMountedRef.current) return
        const currentAccessState = useStudioStore.getState()
        if (
          sameStudioAccess(studiosRef.current, loadedStudios) &&
          currentAccessState.studiosSyncStatus === 'ready'
        ) return
        setStudios(loadedStudios)
      } catch (error) {
        console.warn('AuthProvider: failed to refresh studios access', error)
        if (isMountedRef.current && useStudioStore.getState().studios.length === 0) {
          failStudiosSync(error instanceof Error ? error.message : undefined)
        }
      } finally {
        accessRefreshInFlightRef.current = false
      }
    }

    const intervalId = window.setInterval(() => {
      void refreshStudiosAccess()
    }, ACCESS_REFRESH_INTERVAL_MS)

    const handleWindowFocus = () => {
      void refreshStudiosAccess(true)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshStudiosAccess(true)
      }
    }

    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    void refreshStudiosAccess(true)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [beginStudiosSync, failStudiosSync, isAuthenticated, setStudios, user?.id, user?.telegram_id])

  // ─── Кто работает под этой учёткой (только dev-сборка) ──────────────────────
  // Вход через мок неотличим от настоящего входа владельца учётки, поэтому
  // исполнителя называет сам разработчик через `VITE_DEV_ACTOR_EMAIL`. Резолвим
  // один раз после входа, чтобы трекер экранов не ждал сети (см. @/lib/devActor).
  useEffect(() => {
    if (!import.meta.env.DEV) return
    if (!isAuthenticated || !user?.id) return
    void ensureDevActorResolved()
  }, [isAuthenticated, user?.id])

  // ─── Timezone update on app open ────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return

    const rawInitData = (window as any)?.Telegram?.WebApp?.initData ?? ''
    if (import.meta.env.DEV && (!isInTelegram || isMockInitData(rawInitData))) return

    const update = async () => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        await withAuthRecovery(() => (supabase as any).rpc('update_user_timezone_v2', { p_timezone: tz }))
        await withAuthRecovery(() => (supabase as any).rpc('log_user_app_session_v2', {
          p_app_version: import.meta.env.VITE_APP_VERSION || null,
          p_telegram_webapp_version: (window as any)?.Telegram?.WebApp?.version || null,
          p_telegram_platform: (window as any)?.Telegram?.WebApp?.platform || null,
          p_timezone: tz,
          p_auth_source: 'app-open'
        }))
      } catch { /* non-critical */ }
    }
    update()
  }, [isAuthenticated, user?.id, isInTelegram, location.pathname])

  // ─── Pending invite redirect ────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return

    // Если мы уже на странице инвайта — не перенаправлять по stale токену
    if (location.pathname.startsWith('/invite/')) {
      // InvitePage очистит контекст только после успешного consume, явного отказа
      // или терминального состояния. До этого не трогаем ни sessionStorage, ни localStorage.
      return
    }

    const returnTo = sessionStorage.getItem('returnTo')
    if (returnTo) {
      sessionStorage.removeItem('returnTo')
      // returnTo is handled by router/RequireAuth
    }

    const snapshot = snapshotPendingInvite()
    const pending = selectPendingInvite(snapshot, { requireAutoAccept: true })
    const hasStoredInvite = Boolean(snapshot.sessionToken || snapshot.localToken || snapshot.autoAcceptToken)

    if (!pending && hasStoredInvite) {
      clearPendingInvite()
    }

    if (pending) {
      window.location.href = `/invite/${pending.token}`
    }
  }, [isAuthenticated, location.pathname])

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (isLoading) return <LoadingScreen />

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 text-center">
          <div className="text-red-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Ошибка аутентификации</h2>
          <p className="text-gray-600 mb-4">{authError}</p>
          {shouldRedirectToSite ? (
            <a
              href="https://my.levita.one"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-fixed-blue-600 text-white px-4 py-2 rounded-lg hover:bg-fixed-blue-700 transition-colors"
            >
              Перейти на сайт
            </a>
          ) : (
            <button
              onClick={() => {
                setAuthError(null)
                hasAttemptedAuth.current = false
                isAuthenticating.current = false
                authenticate()
              }}
              className="w-full bg-fixed-blue-600 text-white px-4 py-2 rounded-lg hover:bg-fixed-blue-700 transition-colors"
            >
              Попробовать снова
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      {children}
      <EmailPromptModal />
    </>
  )
}
